import type { DihedralProfile } from "@/api/affdo";
import type { ScanPointGeometry, TorsionScanGeometryResponse } from "@/api/scan-geometry";

/**
 * The reading rules for scan-point geometry — pure, so the four provenance
 * cases can be pinned by a test that mounts nothing (VIZ U5.2b).
 *
 * The contract this implements is
 * `docs/source/dev/reference/scan-geometry-provenance.rst`; it is the
 * authoritative statement and is not restated here. What lives in this module
 * is the CLIENT's side of it: the three-way reading of a provenance flag, the
 * angle join, and the one predicate that decides whether the curve on screen
 * may be annotated at all.
 */

/**
 * How a scan point's geometry reads on screen.
 *
 * **Three values for four wire cases, and that collapse is the contract.**
 * `geometry_source: null` (the record predates the key) and an unrecognised
 * value (a member added after this release) are DIFFERENT facts on the wire —
 * only absence yields `null` — but they are the SAME claim to a reader: this
 * release cannot tell you where this geometry came from. Rendering them
 * differently would invite the reader to infer something from the difference
 * that neither case supports; rendering either as `optimized` is the defect the
 * whole surface exists to prevent.
 */
export type GeometryProvenance = "optimized" | "seed_fallback" | "unknown";

/**
 * Read one point's provenance.
 *
 * **`geometry_source_known` is consulted FIRST and is not derivable here.**
 * `SCAN_GEOMETRY_SOURCES` is append-only documentation on the server, so the
 * set of recognised values is a property of the RELEASE that served the
 * response, not of this bundle. Deciding it client-side — `source ===
 * "optimized" ? … : …` — is precisely the "pattern-match on the two strings I
 * know and treat everything else as optimised" bug that
 * `scan-geometry.test.ts`'s unrecognised-value case is the canary for.
 *
 * The `switch` afterwards is belt-and-braces in the other direction: a value
 * the SERVER recognises but this bundle does not render still lands on
 * `unknown`, never on a default that happens to be `optimized`.
 */
export function classifyGeometryProvenance(point: ScanPointGeometry): GeometryProvenance {
  if (!point.geometry_source_known) return "unknown";
  switch (point.geometry_source) {
    case "optimized":
      return "optimized";
    case "seed_fallback":
      return "seed_fallback";
    default:
      return "unknown";
  }
}

/**
 * What a reader is told about each case, in words.
 *
 * `seed_fallback`'s sentence names BOTH halves of the lie it prevents. The
 * geometry is the pre-DFT seed — byte-for-byte indistinguishable from a real
 * optimised structure — and the energy plotted beside it is not that
 * structure's. Marking only the geometry would leave the plotted point still
 * reading as measured, which is half the defect (VISUALIZATION_PLAN U5.2,
 * Rupert pm#261 r1).
 *
 * **What it does NOT say is where that energy came from** (#1240, Rupert
 * PR-1236 r0). This table is a static `Record` keyed on the geometry flag with
 * no record in hand, and `seed_fallback` means one thing only: this point has
 * no `final_xyz_uri` of its own. The blend of bracketing neighbours it used to
 * assert is real under `unconverged_handling='interpolate'` alone — under
 * `'include'` a point whose optimisation never wrote a geometry wears the same
 * flag while its energy is its own failed DFT number. The policy-licensed
 * reading is stated once, beneath the chart, by `ScanProvenanceNotes`, which
 * has `unconverged_handling` to gate it on.
 */
export const PROVENANCE_COPY: Record<
  GeometryProvenance,
  { readonly label: string; readonly detail: string }
> = {
  optimized: {
    label: "DFT-optimised geometry",
    detail: "This structure was optimised at this angle; the energy beside it is its own.",
  },
  seed_fallback: {
    label: "No optimised geometry — pre-DFT seed structure",
    detail:
      "This record holds no optimised geometry for this angle, so the structure shown is the upstream XTB seed. The energy beside it is not this structure's.",
  },
  unknown: {
    label: "Provenance unknown",
    detail:
      "This record does not say where this geometry came from, so it cannot be read as optimised.",
  },
};

/**
 * Why a point's provenance is unknown, for the one line that says so.
 *
 * Absence and an unrecognised value render IDENTICALLY as a mark — they are one
 * claim (see {@link GeometryProvenance}) — but the reason is worth printing
 * once beside the selected point, because the two have different remedies: a
 * pre-key record is re-scanned, an unrecognised value means this browser bundle
 * is older than the writer. Returns `null` for a point that is not unknown.
 */
export function describeUnknownReason(point: ScanPointGeometry): string | null {
  if (classifyGeometryProvenance(point) !== "unknown") return null;
  if (point.geometry_source === null) {
    return "this record predates the provenance flag";
  }
  return `this release does not recognise the recorded source ${JSON.stringify(point.geometry_source)}`;
}

/**
 * Angle-join tolerance, in degrees.
 *
 * Both grids are JSON floats, and under a fit overlay they were produced by two
 * different writers, so `===` is the wrong predicate for what is physically a
 * degrees value. A scan grid is spaced tens of degrees apart (15° in
 * production, 10° at the densest the plot will mark), so any tolerance well
 * under half a step resolves a point unambiguously or not at all — the join
 * cannot land on the wrong neighbour at this scale, only fail.
 */
export const SCAN_ANGLE_TOLERANCE_DEG = 1e-6;

/**
 * `angleDeg` folded into the half-open interval `[0, 360)`.
 *
 * **The normalisation rule, stated once and applied to BOTH sides of every
 * join.** A dihedral is periodic mod 360°, so `-180°` and `180°` name one
 * geometry, as do `0°` and `360°`, and `-10°` and `350°`. The two grids this
 * client joins are written by different producers — the scan projector emits
 * the record's own grid while `_apply_fit_overlay` substitutes the fit
 * artifact's — and neither the contract nor the schema pins which convention
 * either uses. A raw `a === b`, or even a raw `|a − b| ≤ tol`, therefore MISSES
 * a pair that is the same angle spelled the other way round, and a miss on this
 * surface is not benign: it is a plotted point with no provenance mark on a
 * curve whose entire argument is that an unmarked point is a claim.
 *
 * `[0, 360)` rather than `(-180, 180]` because it is the domain the chart's own
 * x-axis is drawn on (`domain={[0, 360]}`), so a normalised angle and a plotted
 * tick read the same. The `% 360` twice is not redundant — one `%` leaves a
 * negative input negative.
 *
 * Non-finite in, non-finite out: `NaN` must not normalise to `0`, which would
 * join a malformed datum onto the record's first point.
 */
export function normalizeAngleDeg(angleDeg: number): number {
  if (!Number.isFinite(angleDeg)) return angleDeg;
  return ((angleDeg % 360) + 360) % 360;
}

/**
 * The shorter of the two ways round the circle between two angles, in degrees —
 * always in `[0, 180]`.
 *
 * This is what {@link SCAN_ANGLE_TOLERANCE_DEG} is compared against, so the
 * wrap seam at 0°/360° is not a place where the join silently stops working.
 *
 * **The non-finite guard is explicit, and it has to be** (flowstack#1196). The
 * intended behaviour — a malformed angle matches nothing — was previously left
 * to fall out of `NaN` propagation, and it does not: `normalizeAngleDeg` passes
 * a non-finite value through, so `Infinity` gives `delta = Infinity`, `360 -
 * delta = -Infinity`, and `Math.min` selects **`-Infinity`**, which passes every
 * `<= tolerance` and joins the malformed angle to the FIRST point. `NaN` fed
 * through correctly; `±Infinity` inverted the rule into "matches everything".
 * Reasoning that ends at `NaN` is not a guard, so the guard is written down.
 */
export function circularAngleDeltaDeg(a: number, b: number): number {
  if (!Number.isFinite(a) || !Number.isFinite(b)) return Number.NaN;
  const delta = Math.abs(normalizeAngleDeg(a) - normalizeAngleDeg(b));
  return Math.min(delta, 360 - delta);
}

/**
 * The scan point at `angleDeg`, or `null` — "no scan point here" is a real
 * answer, and since VIZ U5.2b r1 it is a rendered one (see
 * {@link ScanMarkReading}).
 *
 * Wrap-aware via {@link circularAngleDeltaDeg}: a scan that records `-180°` and
 * a curve plotted at `180°` are the same geometry and join.
 */
export function scanPointAtAngle(
  points: readonly ScanPointGeometry[],
  angleDeg: number,
  tolerance: number = SCAN_ANGLE_TOLERANCE_DEG
): ScanPointGeometry | null {
  for (const point of points) {
    if (point.angle_deg === null) continue;
    if (circularAngleDeltaDeg(point.angle_deg, angleDeg) <= tolerance) return point;
  }
  return null;
}

/**
 * The scan point CLOSEST to `angleDeg` the short way round, or `null` when no
 * point can be closest to it.
 *
 * **This is not a relaxed {@link scanPointAtAngle} and must never be used as
 * the join.** Pairing a structure with the energy of a different point is the
 * silent wrong render the tolerance exists to refuse, and it looks completely
 * correct doing it. Its one caller is the viewer's "no scan point at this
 * angle" state, where the miss has already been stated in words: this supplies
 * the labelled MOVE the reader may choose, never a substitution made on their
 * behalf.
 *
 * Returns `null` for a non-finite `angleDeg` (nothing is near a malformed
 * angle) and skips points the record gave no angle — both fall out of
 * {@link circularAngleDeltaDeg} returning `NaN`, which loses every `<`
 * comparison, so a miss stays a miss rather than becoming an arbitrary first
 * point. Ties go to the earlier point, which is the scrub's own order.
 */
export function nearestScanPoint(
  points: readonly ScanPointGeometry[],
  angleDeg: number
): ScanPointGeometry | null {
  let nearest: ScanPointGeometry | null = null;
  let nearestDelta = Number.POSITIVE_INFINITY;
  for (const point of points) {
    if (point.angle_deg === null) continue;
    const delta = circularAngleDeltaDeg(point.angle_deg, angleDeg);
    if (delta < nearestDelta) {
      nearestDelta = delta;
      nearest = point;
    }
  }
  return nearest;
}

/**
 * Whether the grid this profile is PLOTTED on is the scan's own.
 *
 * `_apply_fit_overlay` REPLACES `angles_deg` outright with the fit artifact's
 * grid whenever a fit exists, and the fit names no upstream scan. On that path
 * the server sets both zero-floors to `null` explicitly — the runner
 * min-subtracted upstream, so the floors it removed are not in the artifact and
 * a `0.0` would assert a scale nobody measured. On the scan-derived path
 * `_relative` returns the floor it actually applied, which is a finite number
 * for any profile with at least one point.
 *
 * So an absent `qm_zero_kcal_per_mol` on a non-empty profile means "the grid
 * came from somewhere other than the scan projector" — and that reading is
 * deliberately WIDER than "a fit overlay is active". It also catches a profile
 * from an API build that never sent the field, and a materialised
 * `AnalysisReport` snapshot that predates it. All three are cases where this
 * client cannot show that the plotted angles are the scan's, and the answer to
 * every one of them is the same: do not annotate. Failing toward "cannot vouch"
 * is the only direction that is safe on a surface whose subject is honesty.
 *
 * An EMPTY profile returns `false` too, and that costs nothing: there are no
 * marks to draw on a curve with no points.
 */
export function curveGridIsScanGrid(profile: DihedralProfile | undefined | null): boolean {
  if (!profile) return false;
  if (profile.angles_deg.length === 0) return false;
  return typeof profile.qm_zero_kcal_per_mol === "number";
}

/**
 * Why this client may not annotate the plotted curve with per-point provenance
 * — or `null` when it may.
 *
 * Two independent things have to hold before a mark on a curve is a claim this
 * client can back, and they fail for different reasons:
 *
 * 1. **The plotted grid must be the scan's own** ({@link curveGridIsScanGrid}).
 *    Under a fit overlay the angles belong to the `TorsionRefinementResult`,
 *    which records nothing about the scan it was fit to. A re-scan landing
 *    before its fit is recomputed then puts the older scan's grid beside
 *    geometries from the newer scan — and because a re-run normally requests
 *    the same angles, the join succeeds cleanly and every per-point flag still
 *    reads perfectly honestly. No provenance flag can catch that; it is the
 *    gap `scan-geometry-provenance.rst` records as open and hands to this unit.
 * 2. **The curve's own scan record must be confirmed to be this one.** The
 *    caller supplies that confirmation (`curveIsFromThisScan`), because only
 *    the caller knows what its curve came from: the Analysis tab can check the
 *    report's `input_artifact_hashes`, the live workspace pins the identity it
 *    last annotated. `undefined` means "the caller did not check", which is
 *    treated as unconfirmed.
 *
 * The withheld case is NOT "hide the geometry". The scrub still drives the
 * viewer off the scan's own grid, where `content_hash` certifies exactly what
 * is on screen; what is withheld is the JOIN — the claim that a mark on THIS
 * curve describes THAT structure. Refusing the geometry as well would blank the
 * viewer for every fitted torsion, which is most of a finished run, over a
 * transient that may not be present at all.
 *
 * **ONE effect, then the cause** (flowstack#1223 D). The two sentences below
 * are two different situations, and both are true of theirs — but they used to
 * open differently and lead with different blame ("the fit does not record …"
 * vs "this view cannot confirm …"), so a reader who met one on the Analysis tab
 * and the other on the workspace read two competing accounts of who was unable
 * to know. They share a lead clause now: the withheld marks are the same fact
 * on both surfaces, and only the reason differs. Neither reason is softened —
 * the first still names the DATA MODEL (the fit records no scan), the second
 * still names THIS VIEW (the caller did not confirm).
 */
export function curveAnnotationRefusal(
  profile: DihedralProfile | undefined | null,
  scan: TorsionScanGeometryResponse | undefined | null,
  curveIsFromThisScan: boolean | undefined
): string | null {
  if (!scan) return null;
  if (!curveGridIsScanGrid(profile)) {
    return "The points below are not marked on the curve above: those angles come from the fitted profile, and the fit record does not name the scan it was fit to.";
  }
  if (curveIsFromThisScan !== true) {
    return "The points below are not marked on the curve above: this view has not confirmed that the curve was drawn from this scan record.";
  }
  return null;
}

/**
 * How ONE mark on the curve reads — the three provenance readings plus the
 * fourth thing a mark can say.
 *
 * **`no_record` is not a provenance.** It is the answer to a different
 * question: the curve draws a datum at this angle and the scan record has no
 * point there at all, so there is nothing whose provenance could be read. The
 * first cut of this unit rendered that case as NO MARK, which made it the one
 * state on this surface expressed by silence — on a curve whose whole argument
 * is that silence is what got us here (Rupert PR-1193 r0). A reader could not
 * tell it from "this bundle has not finished loading the scan yet", and both
 * sat beside points the same reader had just been taught to read as claims.
 *
 * It is deliberately NOT folded into `unknown`. `unknown` says *there is a
 * geometry here and this release cannot say where it came from*; `no_record`
 * says *there is no geometry here*. Collapsing them would invite a reader to
 * hunt for a structure that does not exist, and would let a scan/curve grid
 * mismatch hide inside a per-point provenance state.
 *
 * The **unannotated** state — this client withheld the marks, or the scan has
 * not arrived — is not a member here on purpose: it is a property of the WHOLE
 * curve, never of one point, and the chart renders it by drawing its neutral
 * grid markers and saying why (`curveAnnotationRefusal`) rather than by
 * choosing a per-point value.
 */
export type ScanMarkReading = GeometryProvenance | "no_record";

/** One plotted angle and how its scan point reads — what the chart marks with. */
export interface ScanPointMark {
  angleDeg: number;
  provenance: ScanMarkReading;
}

/**
 * The marks for a curve, one per PLOTTED angle — **every** finite one.
 *
 * Keyed off the profile's angles rather than the scan's so a mark can only ever
 * sit where the chart actually draws a datum. An angle the scan has no point
 * for gets a `no_record` mark rather than no mark: see {@link ScanMarkReading}
 * for why the absence had to become something a reader can see.
 *
 * A non-finite plotted angle is still skipped, and that is not the same
 * omission — the chart does not draw a datum there either (`toChartData` keeps
 * the raw array, but recharts cannot place a `NaN`), so a mark would have
 * nowhere to sit.
 *
 * The join is wrap-aware (`scanPointAtAngle`), so a `no_record` mark means the
 * scan genuinely has no point at that angle by ANY spelling of it — not that
 * the two grids disagreed about where the seam is.
 */
export function scanPointMarks(
  profile: DihedralProfile,
  points: readonly ScanPointGeometry[]
): ScanPointMark[] {
  const marks: ScanPointMark[] = [];
  for (const angle of profile.angles_deg) {
    if (!Number.isFinite(angle)) continue;
    const point = scanPointAtAngle(points, angle);
    marks.push({
      angleDeg: angle,
      provenance: point === null ? "no_record" : classifyGeometryProvenance(point),
    });
  }
  return marks;
}

/** The points a scrub can step through — those the scan gave a real angle. */
export function scrubbablePoints(
  scan: TorsionScanGeometryResponse | undefined | null
): ScanPointGeometry[] {
  if (!scan?.points) return [];
  return scan.points.filter((p) => p.angle_deg !== null);
}
